/**
 * three.js objects for the non-triangle half of a baked model LOD: line
 * segments, sprite billboards and shaded spheres
 * (docs/formats/models-tbl.md §4.3/§4.4). Split out of
 * `three-model-scene.ts` so that file stays the scene lifecycle.
 *
 * 55 of the corpus's 360 `.TBL` objects have **no triangles at any LOD** —
 * the `TERR*` roads and rivers are pure polylines, and `FLAK`/`EXPLO`/
 * `SMOKE`/`MISC#19` are pure sprites — so without these the viewer shows
 * them as an empty grid, which is exactly the bug this fixes.
 */

import * as THREE from "three/webgpu";
import type { BakedModelBillboard, BakedModelLodBody, BakedModelSphere, BakedModelSpriteSheet } from "../three/baked-model.ts";
import { billboardQuad, metresPerSpritePixel } from "../three/model-sprites.ts";

const RGB_MAX = 255;
const SPHERE_SEGMENTS = 16;
const SPHERE_RINGS = 12;
/** Frames are 8-3 px tall; a hard cut keeps the palette-index-0 surround from ghosting as a grey box. */
const SPRITE_ALPHA_TEST = 0.35;

/** Rotation the viewer applies to model-space geometry (Z up, +Y nose) to stand it upright in three.js's Y-up world. */
const MODEL_X_AXIS = new THREE.Vector3(1, 0, 0);

function toSceneSpace(position: readonly [number, number, number], pitchRadians: number): THREE.Vector3 {
  return new THREE.Vector3(position[0], position[1], position[2]).applyAxisAngle(MODEL_X_AXIS, pitchRadians);
}

/** `THREE.LineSegments` for a LOD's 2-point faces, or `undefined` when it has none. */
export function buildLineSegments(lod: BakedModelLodBody, pitchRadians: number): THREE.LineSegments | undefined {
  const lines = lod.lines;
  if (!lines || lines.indices.length === 0) return undefined;

  // Non-indexed, two vertices per segment: each segment carries its own
  // colour, exactly like `model-geometry.ts` does for triangles.
  const segmentCount = lines.indices.length / 2;
  const positions = new Float32Array(segmentCount * 6);
  const colors = new Float32Array(segmentCount * 6);

  for (let s = 0; s < segmentCount; s++) {
    const rgba = lines.colors[s]?.colorRgba ?? [255, 255, 255, 255];
    for (let end = 0; end < 2; end++) {
      const vertex = lines.indices[s * 2 + end]! * 3;
      const out = s * 6 + end * 3;
      positions[out] = lines.positions[vertex]!;
      positions[out + 1] = lines.positions[vertex + 1]!;
      positions[out + 2] = lines.positions[vertex + 2]!;
      colors[out] = (rgba[0] ?? 0) / RGB_MAX;
      colors[out + 1] = (rgba[1] ?? 0) / RGB_MAX;
      colors[out + 2] = (rgba[2] ?? 0) / RGB_MAX;
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geometry.rotateX(pitchRadians);
  return new THREE.LineSegments(geometry, new THREE.LineBasicMaterial({ vertexColors: true }));
}

/**
 * One `THREE.Mesh` per sphere primitive. Radius and centre are already in
 * metres in the baked JSON, and `colorRgba` is the palette index the
 * 256-colour renderer uses verbatim — 36 (leather brown) for a pilot's head,
 * 133/134 (olive) for the tree canopies.
 */
export function buildSpheres(spheres: readonly BakedModelSphere[], pitchRadians: number): THREE.Mesh[] {
  return spheres.map((sphere) => {
    const [r, g, b] = sphere.colorRgba;
    const material = new THREE.MeshBasicMaterial({ color: new THREE.Color((r ?? 0) / RGB_MAX, (g ?? 0) / RGB_MAX, (b ?? 0) / RGB_MAX) });
    const mesh = new THREE.Mesh(new THREE.SphereGeometry(Math.max(sphere.radius, 1e-4), SPHERE_SEGMENTS, SPHERE_RINGS), material);
    mesh.position.copy(toSceneSpace(sphere.position, pitchRadians));
    return mesh;
  });
}

export interface BillboardObjects {
  /** One group per sprite; the caller re-aims their quaternions at the camera every frame. */
  readonly groups: readonly THREE.Group[];
  readonly texture: THREE.Texture | undefined;
  readonly material: THREE.Material | undefined;
}

function sheetTexture(sheet: BakedModelSpriteSheet): THREE.Texture {
  const texture = new THREE.TextureLoader().load(`/baked/${sheet.path}`);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  // NoColorSpace: the sheet is baked straight from the game's own palette
  // bytes, not a photograph — see `webgpu-support.ts`'s colour-management note.
  texture.colorSpace = THREE.NoColorSpace;
  return texture;
}

function quadGeometry(width: number, height: number, uv: { u0: number; v0: number; u1: number; v1: number }): THREE.BufferGeometry {
  const geometry = new THREE.PlaneGeometry(width, height);
  // PlaneGeometry's uv order is (0,1) (1,1) (0,0) (1,0) — top-left, top-right, bottom-left, bottom-right.
  geometry.setAttribute("uv", new THREE.BufferAttribute(new Float32Array([uv.u0, uv.v1, uv.u1, uv.v1, uv.u0, uv.v0, uv.u1, uv.v0]), 2));
  return geometry;
}

/**
 * Camera-facing quads for a LOD's sprite billboards, one shared texture and
 * material for the whole sheet. The pixel-to-metre scale is a viewer
 * convention documented in `model-sprites.ts` — the original blits these at
 * a fixed pixel size and never scales them with distance.
 */
export function buildBillboards(
  billboards: readonly BakedModelBillboard[],
  sheet: BakedModelSpriteSheet | undefined,
  scale: { readonly boundingRadius: number; readonly maxDetail: number },
  pitchRadians: number,
): BillboardObjects {
  if (billboards.length === 0 || !sheet) return { groups: [], texture: undefined, material: undefined };

  const texture = sheetTexture(sheet);
  const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true, alphaTest: SPRITE_ALPHA_TEST, side: THREE.DoubleSide, depthWrite: false });
  const metresPerPixel = metresPerSpritePixel(scale.boundingRadius, scale.maxDetail);

  const groups: THREE.Group[] = [];
  for (const billboard of billboards) {
    const quad = billboardQuad(billboard, metresPerPixel, sheet);
    if (!quad) continue;
    const mesh = new THREE.Mesh(quadGeometry(quad.width, quad.height, quad.uv), material);
    mesh.position.set(quad.centerOffset[0], quad.centerOffset[1], 0);
    const group = new THREE.Group();
    group.position.copy(toSceneSpace(quad.anchor, pitchRadians));
    group.add(mesh);
    groups.push(group);
  }

  return { groups, texture, material };
}
